/**
 * descreverErro — transforma o que foi lançado num motivo que a operação consegue investigar.
 *
 * Spec e o porquê no topo de `descreverErro.test.ts`.
 *
 * 🔴 Das chaves saem os NOMES, nunca os VALORES. O erro de um upload pode carregar nome de arquivo,
 * e nome de arquivo é dado do apostador. `JSON.stringify` aqui seria vazamento, não telemetria.
 *
 * A defesa principal é NÃO COLETAR: `Error.message` e valores primitivos são texto livre e podem
 * carregar dado do apostador, então nunca saem daqui. Só o que a estrutura do erro garante ser
 * técnico entra no motivo (nome da classe, código/status, tipo e chaves). O backend ainda redige o
 * texto inteiro como segunda barreira. Serializar mais campos aqui seria reabrir o vazamento.
 */

/** Teto próprio, folgado dentro dos 200 do `reportClientError` — não competir com nome e código. */
const MAX = 200;

/**
 * 🔴 CHECAGEM DE FORMA — `name`, `code`, `status` e nome de chave são GRAVÁVEIS em JavaScript.
 *
 * Dizer que "a estrutura do erro garante ser técnico" era falso: nada impede `e.name = <texto do
 * apostador>` nem uma chave montada com dado dele. E filtrar CARACTERE não resolve — tirar os
 * espaços de "nome do apostador" ainda entrega "nomedoapostador", legível. O que separa técnico de
 * texto livre é a FORMA: um identificador único, começando por letra, sem espaço e sem separador.
 * O que não tiver essa forma é descartado inteiro, não "limpo".
 */
const TEM_FORMA_DE_IDENTIFICADOR = /^[A-Za-z][A-Za-z0-9]{0,39}$/;

/*
 * Nomes de erro que a plataforma define. Fora desta lista, só passa quem tem forma de CLASSE DE ERRO
 * (PascalCase terminando em `Error`) — que é como `TwilsockUpstreamError` e `ChunkLoadError` se
 * chamam.
 *
 * 🔴 Por que não basta "forma de identificador": `MariaSilva` e `User11144477735` são identificadores
 * sintaticamente válidos. `e.name` é campo GRAVÁVEL, então forma não separa técnico de dado de
 * pessoa — separa técnico de texto com espaço. A revisão apontou isso duas vezes.
 */
const NOMES_DA_PLATAFORMA = new Set([
    "Error",
    "TypeError",
    "RangeError",
    "ReferenceError",
    "SyntaxError",
    "URIError",
    "EvalError",
    "AggregateError",
    "DOMException"
]);

/*
 * Sufixos que classes de erro de verdade usam. Só `Error` era estreito demais: o SDK da Twilio tem
 * `TwilsockRequestTimeout` e `TwilsockChannelUnavailable`, e rebaixar os dois para "Error" fazia
 * incidentes distintos dividirem a assinatura `Error||` quando não há stack — trocando um problema
 * de PII por um de cegueira.
 */
const TEM_FORMA_DE_CLASSE_DE_ERRO = /^[A-Z][A-Za-z0-9]{1,38}(?:Error|Exception|Timeout|Fault|Failure|Unavailable)$/;

/** O nome, quando ele é reconhecidamente técnico; `"Error"` quando não dá para afirmar isso. */
const nomeTecnicoDoErro = (e: Error): string =>
    typeof e.name === "string" &&
    !TEM_CARA_DE_DOCUMENTO(e.name) &&
    (NOMES_DA_PLATAFORMA.has(e.name) || TEM_FORMA_DE_CLASSE_DE_ERRO.test(e.name))
        ? e.name
        : "Error";

/** Código de fornecedor: dígitos ou identificador curto. Nada de texto com separador ou espaço. */
/*
 * 🔴 Código do fornecedor, e NÃO "qualquer alfanumérico curto".
 *
 * A forma anterior (`/^[A-Za-z0-9]{1,20}$/`) aceitava **um CPF sem pontuação** — 11 dígitos passam.
 * Isso já ia para o texto do alerta, e passou a ir também para a impressão quando o código virou
 * discriminador. Números de código real são status HTTP (3 dígitos) e código da Twilio (5): a faixa
 * 3–5 cobre os dois e exclui documento, telefone e id de conta. Fora disso, exige-se começar por
 * letra, que é a forma de `ETIMEDOUT`/`ERR_NETWORK`.
 */
const TEM_FORMA_DE_CODIGO = /^(?:\d{3,5}|[A-Za-z][A-Za-z0-9_]{1,19})$/;

/** Códigos que valem no motivo: os que apontam O QUE o fornecedor recusou. */
function sufixoDeCodigo(e: Error): string {
    const bruto = e as Error & { code?: unknown; status?: unknown };
    for (const [campo, valor] of [
        ["code", bruto.code],
        ["status", bruto.status]
    ] as const) {
        if (typeof valor === "number" || typeof valor === "string") {
            const texto = String(valor);
            /*
             * 🔴 A trava de documento vale AQUI TAMBÉM, e esquecê-la era pior que no hash: este
             * texto vai em CLARO no motivo, e o motivo chega no WhatsApp da operação. `usr_11144477735`
             * tem forma de código (começa por letra, alfanumérico curto) e carrega um documento
             * inteiro. Achado das duas revisões cruzadas, na mesma rodada.
             */
            if (TEM_FORMA_DE_CODIGO.test(texto) && !TEM_CARA_DE_DOCUMENTO(texto)) {
                return ` (${campo} ${texto})`;
            }
            // Tem code/status, mas não tem forma de código: é texto livre disfarçado. Descarta.
            return "";
        }
    }
    return "";
}

/** Só o formato do que foi lançado — nunca o conteúdo. */
function descreverNaoErro(e: unknown): string {
    if (e === null) return "null lançado";
    if (e === undefined) return "undefined lançado";
    if (Array.isArray(e)) return `array[${e.length}] lançado`;
    if (typeof e === "object") {
        // CHAVES, não valores: dizem onde olhar sem carregar o dado de ninguém.
        const chaves = Object.keys(e as Record<string, unknown>)
            .filter((chave) => TEM_FORMA_DE_IDENTIFICADOR.test(chave))
            .slice(0, 12);
        return `object lançado {${chaves.join(", ")}}`;
    }
    // Primitivo: só o tipo. O valor é texto livre e pode carregar dado do cliente.
    return `${typeof e} lançado`;
}

/**
 * Impressão do erro — identifica o BUG sem transportar o texto dele.
 *
 * 🔴 POR QUE EXISTE. Quando `descreverErro` parou de repassar `Error.message`, todo `Error` comum
 * passou a se descrever igual ("Error: falha técnica"). Isso colapsou DOIS estágios de
 * deduplicação: o `Set` do navegador e a chave de throttle do backend (`evento + motivo`). Dois
 * bugs diferentes dividiam a chave, o segundo alerta era engolido e as linhas de log ficavam
 * indistinguíveis — a operação via o primeiro problema da sessão e mais nada.
 *
 * 🔒 NÃO é o texto do erro, e não pode virar um: é um hash de 32 bits em base36, opaco e não
 * reversível. O primeiro quadro do stack entra porque distingue dois `throw new Error("falhou")`
 * em lugares diferentes do nosso bundle.
 *
 * 🔴 A ENTRADA NÃO PODE DERIVAR DE TEXTO LIVRE — decisão do Mateus em 2026-09-05, gate `compliance`.
 *
 * Ela era `name|message|stack`, e isso a fazia derivar da `Error.message`. Medido: duas mensagens
 * que diferissem por um valor do apostador produziam impressões DIFERENTES para o mesmo defeito. O
 * hash deixava de identificar o ERRO e passava a correlacionar PESSOAS — que é exatamente o que este
 * arquivo inteiro existe para evitar. Nenhum `throw` do nosso bundle interpola dado hoje, mas a
 * `message` de um SDK ou do navegador não é nossa para garantir.
 *
 * ⚠️ O ramo do que NÃO é `Error` continua fazendo `String(e).slice(0, 500)`, que é texto cru — e a
 * decisão de 2026-09-05 NÃO o cobriu. Trocá-lo por `descreverNaoErro` foi TENTADO e MEDIDO: colapsa
 * caminho de produção real. O `window.onerror` entrega `evento.error ?? evento.message`
 * (`instalarHandlersGlobais.ts:150`), e quando o navegador não dá o objeto (script cross-origin,
 * aparelho antigo) o que chega aqui é a STRING da mensagem — então "erro A" e "erro B" passariam a
 * dividir a mesma impressão, e os testes de `instalarHandlersGlobais` acusaram na hora.
 * Está registrado como decisão pendente no plano v2; não mexa sem ela.
 *
 * 🔴 O LIMITE, MEDIDO E NÃO RESOLVIDO (revisão de 2026-09-05, 2ª rodada):
 *
 *  - sem stack E sem código, a entrada vira `Nome||` e TODOS os erros daquele nome colapsam. A
 *    defesa que sobra é o teto de 5 relatos por sessão, não a impressão;
 *  - dois caminhos que só divergem a partir do QUARTO quadro continuam com a mesma impressão. Três
 *    quadros é heurística, não garantia;
 *  - no sentido oposto, um quadro superior instável (wrapper assíncrono, URL dinâmica) pode fazer o
 *    MESMO defeito variar entre sessões e furar o throttle;
 *  - uma mensagem que termine exatamente em `<algo>@<algo>:<n>:<n>`, sem espaço antes do "@", ainda
 *    tem forma de quadro do WebKit. É rebuscado, mas é a fronteira que sobra da regex.
 *
 * Nada disso VAZA dado: são perdas ou ganhos de DISCRIMINAÇÃO. Estão escritos aqui porque a próxima
 * pessoa que investigar "por que só chegou um alerta" precisa achar esta lista antes de suspeitar do
 * backend.
 */
/*
 * Um QUADRO de stack, nas duas gramáticas que os motores usam:
 *   V8 (Chrome):        "    at algo (https://.../main.js:1:2)"   — precedido de um CABEÇALHO
 *   WebKit/Firefox:     "algo@https://.../main.js:1:2"            — SEM cabeçalho
 *
 * 🔴 Pegar `split("\n")[1]` era errado nos dois sentidos, e o segundo é grave: no V8 o índice 1 é o
 * primeiro quadro, mas no WebKit — o motor do público deste produto, que é celular — ele é o
 * SEGUNDO. O discriminador mudava de significado conforme o navegador.
 *
 * E o cabeçalho do V8 é `"Nome: mensagem"`, ou seja, carrega a MENSAGEM. Reconhecer quadro por FORMA
 * (e não por posição) é o que garante que ele nunca volte para dentro do hash pela porta dos fundos.
 */
/*
 * 🔴 OS DOIS RAMOS PRECISAM DA POSIÇÃO `:linha:coluna`, e a revisão mostrou por quê.
 *
 * A versão anterior era `/^at\s|@.+:\d+:\d+$/`, e a alternância tem precedência mínima: o primeiro
 * ramo era só `^at\s`, sem exigir mais nada. Uma linha de MENSAGEM multilinha como
 * `"at step validateDocument: CPF 11144477735"` passava por quadro e ia direto para o hash. O
 * segundo ramo não tinha âncora de início, então o cabeçalho `"Error: falhou para a@b.com:12:34"`
 * também passava — devolvendo a mensagem inteira para dentro do cálculo.
 *
 * Quadro de verdade termina em `:linha:coluna` nos dois motores. Exigir isso nos dois ramos é o que
 * separa quadro de texto do autor.
 */
/*
 * 🔴 O NORMALIZADOR: tudo que é texto livre e entra no hash passa por aqui primeiro.
 *
 * As duas rodadas de revisão mostraram que caçar padrão a padrão não fecha — UUID com hífen,
 * CPF pontuado, slug com nome, id de sessão em URL `blob:`, cada um exigia uma regra nova. A ideia
 * certa veio da revisão: **redigir, não descartar**. O TEMPLATE da mensagem sobrevive (é ele que
 * separa dois defeitos), e os VALORES viram marcador (são eles que separam duas pessoas).
 *
 * A ordem importa: e-mail antes de número, senão o domínio é comido primeiro.
 */
const PADROES_DE_VALOR: RegExp[] = [
    /[\w.+-]+@[\w-]+\.[\w.-]+/g, // e-mail
    /[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi, // uuid (id de sessão em blob:)
    /*
     * 🔴 A ORDEM AQUI É MEDIDA, não estética. O hexadecimal vem ANTES do numérico: dígito é
     * subconjunto de hexadecimal, então `\d{4,}` rodando primeiro FATIA um token de 32 chars em
     * pedaços menores que 12, e o padrão hex nunca mais casa. Medido: `e5a81234bf7c91d2c0011234abcd5678`
     * virava `e5a#bf7c91d2c#abcd#` — fragmentos sobrevivendo, e duas sessões continuando distintas.
     * Com a ordem certa vira `#`.
     *
     * O e-mail continua antes de tudo pelo mesmo motivo invertido: `joao1234@x.com` viraria
     * `joao#@x.com` e deixaria de casar como e-mail.
     */
    /[0-9a-f]{12,}/gi, // hash/id hexadecimal — 12+, para não comer content hash curto de chunk
    /*
     * 🔴 FORMAS ESPECÍFICAS, e não "dígito com pontuação". A classe genérica que eu tinha escrito
     * (`\d[\d.\-\s()\/+]{6,}\d`) colapsava DIAGNÓSTICO: medido, `HTTP 400 / 404` e `HTTP 500 / 503`
     * viravam a MESMA impressão — uma queda de infra mascarada por um erro rotineiro de cliente —, e
     * `/api/v2/1001/2002/x.js` perdia a profundidade da rota. Redigir demais é o erro OPOSTO, e nesta
     * tarefa ele já me mordeu duas vezes.
     */
    /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/g, // CNPJ pontuado
    /*
     * Grupos de dígitos separados por ponto: cobre CPF, RG, PIS/PASEP e as variações sem traço, sem
     * precisar de um padrão por documento. Medido contra o que NÃO pode colapsar: `HTTP 400 / 404`,
     * `/api/v2/1001/2002/`, `1.2.3`, `192.168.0.1` e `main.a1b2c3d4.js` passam intactos, porque todos
     * têm grupo de 1 dígito, separador que não é ponto, ou letra no meio.
     */
    /\d{2,3}(?:\.\d{2,5}){2,}(?:[-\/]\d{1,4})?/g,
    /\(?\d{2}\)?[\s.-]?\d{4,5}[\s.-]?\d{4}/g, // telefone, com ou sem DDD e pontuação
    /\d{4,}/g, // qualquer sequência numérica longa que sobre
    /"[^"]*"|'[^']*'/g // valor entre aspas
];

const semValores = (texto: string): string =>
    PADROES_DE_VALOR.reduce((atual, padrao) => atual.replace(padrao, "#"), texto);

/** Uma sequência longa de dígitos num campo dito "técnico" é sinal de documento, não de código. */
/*
 * 🔴 CONTA os dígitos, não exige que sejam contíguos. A versão anterior era `/\d{6,}/`, e a própria
 * gramática de código aceita `_`: `usr_111_444_777_35` tem onze dígitos e nenhuma sequência de seis,
 * então passava — e este caminho vai em TEXTO CLARO no motivo do alerta.
 */
/**
 * 🔒 Seis dígitos ou mais = documento, não código. CPF, telefone e cartão cabem em texto que TEM
 * forma de código (`usr_11144477735` começa por letra e é curto), e este texto vai em claro para o
 * WhatsApp da operação.
 *
 * Exportada porque a mesma decisão vale para os campos ESTRUTURADOS (`extrairCodigos`, em
 * `diagnosticoDeEnvio.ts`), que seguem pelo mesmo caminho até o mesmo destino. Duas cópias da regra
 * é como um destino fica para trás — foi o que aconteceu aqui: o sufixo do motivo ganhou a trava e o
 * campo estruturado ficou sem, deixando um CPF de 11 dígitos passar (achado da revisão, 2026-09-07).
 */
export const temCaraDeDocumento = (texto: string): boolean => (texto.match(/\d/g) ?? []).length >= 6;

const TEM_CARA_DE_DOCUMENTO = temCaraDeDocumento;

/*
 * Quadro precisa ter POSIÇÃO **e** URL — ou ser a forma anônima/nativa que os motores emitem.
 *
 * Exigir só `:linha:coluna` não bastava: `at step validateDocument: CPF 111.444.777-35:12:34` e
 * `contato@exemplo.com:12:34` continuavam passando, e são texto do autor. Nenhum dos dois tem `//`.
 *
 * E a forma anônima entra por um ramo próprio porque `at Array.map (<anonymous>)` é quadro LEGÍTIMO e
 * comum no V8 — rejeitá-lo (como a versão anterior fazia) esvaziava o discriminador em stacks
 * inteiros de callback.
 */
/*
 * 🔴 TIRA OS VALORES DA URL DO QUADRO, preservando o `:linha:coluna`.
 *
 * O widget é injetado no DOM do site da marca SEM iframe. Um `unhandledrejection` que atravesse
 * código da página hospedeira traz o quadro DELA no stack — e a URL dela leva query string, e o
 * caminho dela pode levar documento ou id de sessão. Sem esta limpeza o dado do apostador entra no
 * cálculo do hash pela porta de trás.
 *
 * O `:linha:coluna` é recortado ANTES e recolado depois porque ele é o discriminador que sobra: um
 * corte ingênuo de `?` até o fim levaria a posição junto e faria dois bugs da mesma URL colidirem.
 */
const semDadosNaUrl = (quadro: string): string => {
    const posicao = quadro.match(/:\d+:\d+\)?$/);
    const semCauda = posicao ? quadro.slice(0, quadro.length - posicao[0].length) : quadro;
    const corte = semCauda.search(/[?#]/);
    const semConsulta = corte >= 0 ? semCauda.slice(0, corte) : semCauda;
    return semValores(semConsulta) + (posicao ? posicao[0] : "");
};

/*
 * 🔴 A GRAMÁTICA DO QUADRO, não "começa com `at` e tem um esquema em algum lugar".
 *
 * A versão anterior aceitava `at cliente Maria https://cdn/x.js:12:34` — uma linha de MENSAGEM que
 * por acaso cite uma URL. O nome sobrevivia ao normalizador e voltava para o hash, que é a mesma
 * correlação de pessoa que tirar a `message` pretendia impedir.
 *
 * As três formas que os motores realmente emitem:
 *   `at <função> (<local>:linha:coluna)`   — V8 com nome de função (que PODE ter espaço:
 *                                            `at new Promise`, `at async foo`)
 *   `at <local>:linha:coluna`              — V8 sem nome; o local é um token só, sem espaço
 *   `<função>@<local>:linha:coluna`        — WebKit/Firefox
 * Mais as formas anônima e nativa, onde o local não é uma URL.
 */
const ehQuadroDeStack = (linha: string): boolean =>
    /^at\s.+\s\(\S+:\d+:\d+\)$/.test(linha) ||
    /^at\s\S+:\d+:\d+$/.test(linha) ||
    /^\S*@\S+:\d+:\d+$/.test(linha) ||
    /*
     * O nome de função do V8 é UM token, com os prefixos `new ` e `async ` como únicas exceções
     * (`at new Promise (<anonymous>)`). Aceitar `.+` aqui reabria `at cliente Maria (<anonymous>)`,
     * que é frase; exigir um token só rejeitava o `new Promise`, que é quadro legítimo. A gramática
     * do motor resolve os dois — foi a tensão que a 5ª rodada apontou.
     */
    /^at\s(?:new\s|async\s)?\S+\s\((?:<anonymous>|native)\)$/.test(linha) ||
    /^\S+@\[native code\]$/.test(linha);

/*
 * Quadros reconhecidos por FORMA — e é isso que mantém a MENSAGEM fora do hash mesmo quando ela é
 * MULTILINHA. Medido: `new Error("Falha:\nCPF: 111…")` no V8 produz um stack cuja linha de índice 1
 * é a SEGUNDA LINHA DA MENSAGEM, com o dado do apostador. A versão que pegava por posição punha isso
 * direto no hash.
 */
const quadrosDoStack = (stack: string, mensagem: string): string => {
    /*
     * 🔴 AS LINHAS DA MENSAGEM SAEM ANTES DA BUSCA POR QUADRO.
     *
     * Nenhuma regex distingue um quadro de uma frase que o IMITA: `at cliente Maria (https://x.js:1:2)`
     * tem a gramática exata do V8, e a revisão demonstrou isso depois de eu apertar a regex
     * três vezes. Mas a mensagem é CONHECIDA — e usá-la para EXCLUIR nunca a coloca dentro do hash,
     * que é justamente a garantia que este arquivo precisa manter.
     */
    const linhasDaMensagem = new Set(
        mensagem
            .split("\n")
            .map((linha) => linha.trim())
            .filter(Boolean)
    );

    return stack
        .split("\n")
        .map((linha) => linha.trim())
        .filter((linha) => !linhasDaMensagem.has(linha))
        .filter(ehQuadroDeStack)
        .slice(0, 3)
        .map(semDadosNaUrl)
        .join(";");
};

/*
 * `code` E `status`, os dois — `sufixoDeCodigo` devolve no PRIMEIRO que encontra, e por isso dois
 * erros com o mesmo `code` e `status` diferentes ficavam idênticos. Pior: `code` presente mas fora de
 * forma fazia a função sair com `""` sem nunca olhar um `status` válido. Aqui a impressão precisa dos
 * dois; o texto do alerta continua com o sufixo curto do `descreverErro`, que não muda.
 */
const codigosParaImpressao = (e: Error): string => {
    const bruto = e as Error & { code?: unknown; status?: unknown };
    return (["code", "status"] as const)
        .map((campo) => {
            const valor = bruto[campo];
            if (typeof valor !== "number" && typeof valor !== "string") return "";
            const texto = String(valor);
            return TEM_FORMA_DE_CODIGO.test(texto) && !TEM_CARA_DE_DOCUMENTO(texto) ? `${campo}:${texto}` : "";
        })
        .join(",");
};

export function impressaoDoErro(e: unknown): string {
    let bruto: string;
    try {
        /*
         * Três quadros, não um: um helper CENTRAL que lança para causas diferentes produz sempre o
         * mesmo primeiro quadro — é o caso do SDK da Twilio, que cria o erro de upstream numa única
         * instrução para qualquer resposta não-2xx. Com um quadro só, duas falhas distintas viravam a
         * mesma impressão e a segunda era descartada pela dedup DA SESSÃO INTEIRA, que é pior que o
         * throttle de 5 min do backend.
         *
         * O código/status entra pelo mesmo motivo, e é o discriminador que sobra quando não há stack
         * nenhum. Ele já é considerado técnico e seguro: é o que o `descreverErro` publica no motivo.
         *
         * O nome passa pela MESMA sanitização do `descreverErro` — `name` é gravável, e um nome fora
         * de forma de identificador não entra cru aqui do mesmo jeito que não entra lá.
         */
        bruto =
            e instanceof Error
                ? [
                      nomeTecnicoDoErro(e),
                      codigosParaImpressao(e),
                      quadrosDoStack(e.stack ?? "", e.message ?? "")
                  ].join("|")
                : `${typeof e}|${semValores(String(e).slice(0, 500))}`;
    } catch {
        return "semimpressao";
    }
    let h = 2166136261;
    for (let i = 0; i < bruto.length; i += 1) {
        h ^= bruto.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(36);
}

export function descreverErro(e: unknown): string {
    const texto =
        e instanceof Error
            ? `${nomeTecnicoDoErro(e)}: falha técnica${sufixoDeCodigo(e)}`
            : descreverNaoErro(e);
    return texto.slice(0, MAX);
}
